import { TrackingEvent, TrackingRegionName, commonConstants } from "constants/index";
import { commonUtils } from "utils";

const { chainMultipleFunctions, capitalize, removeSpecialChar } = commonUtils;
const { API_RESPONSE_TIME_BUCKETS } = commonConstants;

export interface TrackUserClickUserEventParams {
  contentName?: string;
  regionName?: string;
  target?: string;
  payload?: string;
}

const trackUserClickEvent = (params: TrackUserClickUserEventParams) => {
  window.track(TrackingEvent.manualTrackInteractionContent, { ...params, interaction: "click" });
};

const trackReturnDrawer = (params: TrackUserClickUserEventParams) => {
  const { contentName, payload } = params;
  trackUserClickEvent({
    contentName,
    regionName: TrackingRegionName.ReturnDrawer,
    payload,
  });
};

export const computeTrackingScreenName = (path: string) => {
  const PATTERN_REGEX = /[/:]/g;
  const replacedPattern: Record<string, string> = {
    "/": ".",
    ":": "&",
  };
  return path.replace(PATTERN_REGEX, (value) => replacedPattern[value]);
};

export const nestRegionName = (...regionNames: (string | undefined)[]) => regionNames.filter(Boolean).join(".");

const convertTabToTrackingName = (str?: string) => {
  if (!str) return "";
  const tabName = chainMultipleFunctions(removeSpecialChar, capitalize, (str: string) => str + "Tab");
  return tabName(str);
};

const getTrackingProps = ({ contentName, regionName, target, payload }: TrackUserClickUserEventParams) => {
  return {
    "data-track-content": true,
    "data-content-region-name": regionName || "root",
    ...(contentName && { "data-content-name": contentName }),
    ...(target && { "data-content-target": target }),
    ...(payload && { "data-content-payload": payload }),
  } as any;
};

const getBucket = (responseTime: number) => {
  let low = 0;
  let high = API_RESPONSE_TIME_BUCKETS.length - 1;

  while (low < high) {
    const mid = Math.floor((low + high) / 2);

    if (responseTime <= API_RESPONSE_TIME_BUCKETS[mid].value) {
      high = mid;
    } else {
      low = mid + 1;
    }
  }

  return API_RESPONSE_TIME_BUCKETS[low].label;
};

const nonBlockTrackResponseTime = (contentName: string, duration: number, property?: Record<any, any> | string) => {
  setTimeout(() => {
    track("customEvent", contentName, {
      action: "apiCallTime",
      category: "erp-web-app",
      label: getBucket(duration),
      value: duration,
      property: property ? (typeof property === "string" ? property : JSON.stringify(property)) : undefined,
    });
  }, 0);
};

const nonBlockTrackInvalidRespTime = (contentName: string, property?: Record<any, any>) => {
  setTimeout(() => {
    track("customEvent", contentName, {
      action: "invalid-apiCallTime",
      category: "erp-web-app",
      property: property ? JSON.stringify(property) : undefined,
    });
  }, 0);
};

const nonBlockTrackRespTimeWithStartAt = (url?: any, startAt?: any, property?: Record<any, any>) => {
  const contentName = String(url) || commonConstants.UNDEFINED_URL;
  const startAtNum = Number(startAt);

  if (!!startAt && !isNaN(startAtNum)) {
    const duration = Math.round(performance.now()) - startAtNum;
    nonBlockTrackResponseTime(contentName, duration, property);
  } else {
    nonBlockTrackInvalidRespTime(contentName, property);
  }
};

const nonBlockTrackResponseTimeWrapper = async <T>(apiCall: Promise<T>, contentName: string): Promise<T> => {
  const startTime = Math.round(performance.now());
  let response = null;

  try {
    response = await apiCall;
  } catch (error) {
    throw error;
  }

  const endTime = Math.round(performance.now());
  const duration = endTime - startTime;

  nonBlockTrackResponseTime(contentName, duration);
  return response;
};

export default {
  trackUserClickEvent,
  trackReturnDrawer,
  computeTrackingScreenName,
  nestRegionName,
  convertTabToTrackingName,
  getTrackingProps,
  nonBlockTrackResponseTime,
  nonBlockTrackInvalidRespTime,
  nonBlockTrackRespTimeWithStartAt,
  nonBlockTrackResponseTimeWrapper,
};
